import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Search, Star, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  useCennikFacety,
  useCennikMaterialy,
  useCennikMaterialyStrankovane,
  useMaterialDetail,
  useStruktury,
  type SirkaTrieda,
} from "@/api/hooks/useCennik";
import { useDebounce } from "@/lib/useDebounce";
import {
  cenaZaMj,
  cisloMm,
  formatMj,
  materialPrimaryId,
  menoKovania,
  rozmerPolozky,
} from "@/lib/format";
import { sediaSlova } from "@/lib/hladanie";
import { formatKategoria } from "@/lib/kategorie";
import { cn } from "@/lib/utils";
import { ANY } from "@/components/cennik/cennikZalozky";
import { kategorieMoznosti } from "@/components/cennik/CennikSidebar";
import { DvojickyZnak } from "@/components/cennik/DvojickyZnak";
import { SetZnak } from "@/components/cennik/SetZnak";
import { StavZnaky } from "@/components/cennik/StavZnaky";
import {
  FILTER_TLACIDLO,
  FILTER_ZAPNUTY,
  KategoriaVyberu,
} from "@/components/vyber/KategoriaVyberu";
import {
  klavesaVyberu,
  TabulkaVyberu,
  type AkoOznacene,
  type RiadokVyberu,
  type SkupinaVyberu,
} from "@/components/vyber/TabulkaVyberu";
import type { DruhMaterialu, Material, MaterialSummary, Sekcia } from "@/api/schemas";
import { jeTovar, podnazovTovaru } from "@/lib/tovar";

/** Kandidát, ktorého appka sama našla, aj s tým prečo (KOLO 120). */
export interface Zhoda {
  material_id: string;
  skore: number;
  /** „8681 + hrúbka 18 mm" — v stĺpci ZHODA ako `title`. */
  dovod: string;
}

/**
 * Položka cenníka v riadku výberu — celý materiál zo zoznamu alebo súhrn
 * (hrany zákazky, odporúčané pre materiál), ktorý štítky nemusí mať.
 */
export type PolozkaCennika = MaterialSummary & Partial<Material>;

/** Riadok skupiny nad cenníkom — položka a čo k nej patrí navyše. */
export interface PolozkaSkupiny {
  material: PolozkaCennika;
  /** „na dielci", „tenká · 18 mm", „manuál". */
  stitky?: ReactNode;
  /** Posledný stĺpec — „4× · 3,8 m". */
  info?: ReactNode;
}

/**
 * Skupina riadkov nad cenníkom (KOLO 201) — „V zákazke", „Odporúčané pre
 * materiál". Bodky zhody sú len v importe; inde odporúčanie ukáže skupina.
 */
export interface SkupinaPoloziek {
  kluc: string;
  nadpis: string;
  polozky: PolozkaSkupiny[];
}

export interface MaterialSelectProps {
  value: string | null;
  /**
   * Pri výbere zo zoznamu cenníka príde aj celý materiál (pre kategóriu
   * ap.); riadok skupiny (`skupiny`) je len súhrn, príde len id.
   */
  onChange: (id: string | null, material?: Material) => void;
  /** Default filter — pre slot materiál typicky "plosny". */
  druh?: DruhMaterialu;
  /**
   * Sekcia cenníka, z ktorej sa vyberá (KOLO 166) — to isté pravidlo
   * ako záložky Cenníka (`services/cennik_sekcie`). Dĺžkový slot tak
   * neponúkne hrany ani krytky a picker hrán neponúkne pracovné dosky
   * bez zoznamu prefixov na frontende. Sklo len pri `sekcia="sklo"`.
   */
  sekcia?: Sekcia;
  /**
   * Bez hrán, inak všetky sekcie druhu (KOLO 166) — dĺžkový dielec
   * ponúka hranol aj dĺžkové kovanie (LED profil, tyč, krytky v m).
   */
  bezHran?: boolean;
  /** Filter hrúbky dosky (z_mm) — napr. "18". Iba pre plošné. */
  zMm?: string;
  /** Trieda hrúbky dosky — 18 ponúkne aj 18,7 (KOLO 188). Iba pre plošné. */
  triedaHrubky?: number;
  /** Filter šírky hrany (sirka_mm) — napr. "22". Iba pre dĺžkové. */
  sirkaMm?: string;
  /**
   * Zo `sirkaMm` spraví pásmo (KOLO 122).
   *
   * Bez neho je filter presná rovnosť a okno skryje aj hranu, ktorú by
   * appka vybrala sama — automatický výber si toleruje pár mm, lebo 42
   * aj 43 olepí 36 mm dosku. **O koľko presne, vie len backend** —
   * číslo tu zámerne nie je, druhá kópia by sa rozišla.
   */
  sirkaPasmo?: boolean;
  /**
   * Trieda šírky hrany (KOLO 222) — Úzka / Stredná / Stredne široká /
   * Široká / Blok. Rozsah tried drží backend (`kod_materialu.SIRKOVE_PASMA`).
   */
  sirkaTrieda?: SirkaTrieda;
  /**
   * Potrebná šírka hrany pre dosku (KOLO 222) — okno ukáže hrany, ktoré
   * dosku olepia, aj širšie (42 aj blok na 25 mm doske). Toleranciu
   * pridá backend, rovnako ako pri `sirkaPasmo`.
   */
  sirkaPostacuje?: string;
  /** Filter hrúbky hrany (hrubka_mm) — napr. "1". Iba pre dĺžkové. */
  hrubkaMm?: string;
  /**
   * CSV prefixov kategórií, v ktorých sa **jedine** hľadá — napr.
   * "PRACOVNA_DOSKA,ZASTENA" pre pracovné dosky z importu. Zúženie
   * v rámci sekcie; to, do ktorej sekcie položka patrí, rieši `sekcia`.
   */
  kategoriaIncludeCsv?: string;
  placeholder?: string;
  /** Kurzor rovno do vyhľadávania (napr. slot dialóg). */
  autoFocus?: boolean;
  /**
   * Kandidáti, ktorých appka sama našla — vykreslia sa **hore v tej
   * istej tabuľke** so stĺpcom Zhoda, pod nimi zvyšok cenníka (KOLO 120).
   *
   * Posiela ich len importné okno. Celé riadky sa doťahujú jedným
   * dopytom cez `ids_csv`, takže pre ne platia **tie isté filtre** ako
   * pre zvyšok: nesprávnu hrúbku odfiltruje SQL, nie tento komponent.
   */
  zhody?: Zhoda[];
  /**
   * Skupiny nad cenníkom (KOLO 201) — hrany zákazky, odporúčané pre
   * materiál. Hľadanie ich filtruje v prehliadači tým istým pravidlom
   * ako server (všetky slová, bez diakritiky); položka zo skupiny sa
   * v cenníku pod ňou neopakuje.
   */
  skupiny?: SkupinaPoloziek[];
  /** Položky, ktoré sa neponúknu vôbec — hrana, ktorá sa práve vymieňa. */
  vynechat?: readonly string[];
  /** Nadpis posledného stĺpca (napr. „V zákazke"). */
  infoNadpis?: string;
  /**
   * Zbalený režim — zoznam sa ukáže až po kliknutí do vyhľadávania.
   *
   * Vždy otvorený zoznam (`max-h-64`) robí z jedného riadku ~384 px.
   * V dialógoch, kde je pickerov desať pod sebou, sa tak na obrazovku
   * zmestí jeden a pol. Zbalený je riadok ~72 px a rozbalí sa len ten,
   * s ktorým práve pracuješ.
   */
  kompaktny?: boolean;
  /**
   * Ponúkni aj riadky, ktoré ustupujú preferovanému dodávateľovi
   * (KOLO 166) — napr. náhrada v objednávke, kde ide práve o iného
   * dodávateľa. Bez neho to rozhoduje prepínač „Aj iní dodávatelia".
   */
  vsetciDodavatelia?: boolean;
  /**
   * Dvojklik alebo Enter na riadku = vyber a potvrď (KOLO 169, 201).
   * Posiela ho okno s vlastným tlačidlom Vybrať; vo formulári je výber
   * riadku sám rozhodnutím.
   */
  onPotvrd?: (material: PolozkaCennika) => void;
  /**
   * Obsah okna `VyberDialog` (KOLO 201) — pás „Vybrané" je v pätičke
   * okna pri tlačidlách, zoznam vyplní výšku okna.
   */
  vOkne?: boolean;
  /**
   * Vyšší zoznam (KOLO 212) — v okne Upraviť materiál je výber dosky
   * hlavná vec, `max-h-64` v ňom ukázal päť riadkov.
   */
  vysokyZoznam?: boolean;
  /** Ďalšie tlačidlá do riadku filtrov (šírka a hrúbka hrany). */
  filtreNavyse?: ReactNode;
  /**
   * Rozmerové filtre ako kapsule s ✕ — predvolene áno. Kto chce vidieť
   * aj iné rozmery, filter vypne vedome, nie tým, že píše (KOLO 120).
   * Výber s vlastnými rozbaľovačkami rozmeru (`filtreNavyse`) ich vypne.
   */
  rozmerKapsule?: boolean;
  /**
   * Volajúci ešte nevie filtre (šírka pásky čaká na tabuľku hrúbok
   * dosiek) — cenník sa nepýta, inak by prišiel dvakrát a zoznam by
   * bez filtra bliknul.
   */
  pripraveny?: boolean;
  /**
   * Kategória, s ktorou výber začne (KOLO 202) — Korpus Builder otvorí
   * Nohy či Výsuvy, výmena kovania kategóriu starého. Zruší sa ako každý
   * iný filter; nová hodnota od volajúceho ju nastaví znova.
   */
  pociatocnaKategoria?: string;
  /**
   * ★ Obľúbené zapnuté hneď pri otvorení (KOLO 202, len kovanie) — to,
   * čo bola v starom výbere kovania kapsula „Všetky obľúbené".
   */
  oblubenePredvolene?: boolean;
  /** Tlačidlo na konci každého riadku — ★ na stránke Kovania (KOLO 202). */
  akciaRiadku?: (material: PolozkaCennika) => ReactNode;
}

/** Kľúč názvu štruktúry jedného výrobcu v mape `useStrukturaLabels`. */
function klucVyrobcu(kod: string, vyrobca: string): string {
  return `${kod}|${vyrobca.toLowerCase()}`;
}

/**
 * Názvy štruktúr z katalógu (SM → SemiMatt…).
 *
 * Identita štruktúry je (kód, výrobca) — KOLO 168: `SM` je u Eggeru
 * „SemiMatt", u Pfleiderera „Semi Matt", `PA` raz PerfectSense, raz
 * Palermo. Mapa má preto kľúč `kód|výrobca` a pod samotným kódom náhradu
 * pre riadky bez výrobcu štruktúry (hrany, dosky Trendu): pri kóde dvoch
 * výrobcov oba názvy, nie náhodne posledný. „Nezaradené" kódy názov nemajú.
 */
export function useStrukturaLabels(): Map<string, string> {
  const strukturyQuery = useStruktury();
  return useMemo(() => {
    const map = new Map<string, string>();
    const podlaKodu = new Map<string, string[]>();
    for (const s of strukturyQuery.data?.data ?? []) {
      if (s.nazov === s.kod) continue;
      map.set(klucVyrobcu(s.kod, s.vyrobca), s.nazov);
      const nazvy = podlaKodu.get(s.kod) ?? [];
      if (!nazvy.includes(s.nazov)) nazvy.push(s.nazov);
      podlaKodu.set(s.kod, nazvy);
    }
    for (const [kod, nazvy] of podlaKodu) map.set(kod, nazvy.join(" / "));
    return map;
  }, [strukturyQuery.data]);
}

/**
 * "SM" → "SM SemiMatt" (alebo len kód, ak katalóg názov nepozná).
 *
 * S výrobcom (`material.vyrobca`) jeho názov, inak náhrada podľa kódu.
 */
export function formatStruktura(
  kod: string | null | undefined,
  labels: Map<string, string>,
  vyrobca?: string | null,
): string | null {
  if (!kod) return null;
  const nazov =
    (vyrobca ? labels.get(klucVyrobcu(kod, vyrobca)) : undefined) ?? labels.get(kod);
  return nazov ? `${kod} ${nazov}` : kod;
}

/**
 * Riadok výberu z položky cenníka — jeden pre všetky výbery (KOLO 201).
 * Pod názvom sivým pravý názov kovania, výrobca a kategória; štítky
 * štruktúra, Set, výpredaj, „nie je v cenníku" a „aj u:" ako v Cenníku.
 */
export function riadokPolozky(
  m: PolozkaCennika,
  strukturaLabels: Map<string, string>,
  navyse: { stitky?: ReactNode; info?: ReactNode; zhoda?: Zhoda; akcia?: ReactNode } = {},
): RiadokVyberu {
  const meno = menoKovania(m);
  const kod = materialPrimaryId(m);
  const struktura = formatStruktura(m.struktura_povrchu, strukturaLabels, m.vyrobca);
  // Tovar (KOLO 230): povrch a karta ABRY — tri „Konzola 20" (CO, GR, BC)
  // by sa inak nedali rozlíšiť
  const pod = jeTovar(m)
    ? podnazovTovaru(m)
    : [meno.pod, m.vyrobca, m.kategoria ? formatKategoria(m.kategoria) : null]
        .filter(Boolean)
        .join(" · ");
  return {
    kluc: m.id,
    kod,
    nazov: meno.hlavne,
    nazovTitle: meno.pod ? `${meno.hlavne} — ${meno.pod}` : m.nazov,
    pod,
    stitky: (
      <span className="flex min-w-0 items-center gap-1 overflow-hidden [&>*]:ml-0 [&>*]:shrink-0 [&>*]:flex-nowrap">
        {m.je_set ? <SetZnak /> : null}
        {struktura ? (
          <span className="whitespace-nowrap rounded-full bg-background-alt px-1.5 text-[10px] font-normal leading-4 text-foreground/80">
            {struktura}
          </span>
        ) : null}
        <StavZnaky material={m} />
        <DvojickyZnak material={m} />
        {navyse.stitky}
      </span>
    ),
    rozmer: rozmerPolozky(m),
    cena: cenaZaMj(m.nakup_cena, m.mj ? formatMj(m.mj) : null),
    info: navyse.info,
    akcia: navyse.akcia,
    zhoda: navyse.zhoda ? { skore: navyse.zhoda.skore, dovod: navyse.zhoda.dovod } : null,
    ariaLabel: `${kod ?? ""} ${meno.hlavne}`.trim(),
  };
}

/** Rozmerový filter, ktorý sa dá kapsulou vypnúť. */
type RozmerKluc = "hrubka" | "sirka" | "paska";

/** Štítok „na dielci", „tenká · 18 mm" — jeden vzhľad pre všetky skupiny. */
export function StitokRiadku({
  children,
  title,
  vyrazny = false,
}: {
  children: ReactNode;
  title?: string;
  vyrazny?: boolean;
}): JSX.Element {
  return (
    <span
      title={title}
      className={cn(
        "whitespace-nowrap rounded-full px-1.5 text-[10px] leading-4",
        vyrazny
          ? "bg-background-group font-bold text-foreground"
          : "border border-input bg-background font-normal text-foreground/80",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Výber položky z cenníka — hľadanie, filtre a tabuľka (KOLO 201: jeden
 * dizajn pre materiál, hranu, kovanie aj import). Server-side search cez
 * `/api/cennik/materialy`.
 */
export function MaterialSelect({
  value,
  onChange,
  druh,
  sekcia,
  bezHran = false,
  zMm,
  triedaHrubky,
  sirkaMm,
  sirkaPasmo,
  sirkaTrieda,
  sirkaPostacuje,
  hrubkaMm,
  kategoriaIncludeCsv,
  placeholder = "Vyhľadať materiál (názov, kód, dekor)…",
  autoFocus = false,
  kompaktny = false,
  zhody,
  skupiny,
  vynechat,
  infoNadpis,
  vsetciDodavatelia = false,
  onPotvrd,
  vOkne = false,
  vysokyZoznam = false,
  filtreNavyse,
  rozmerKapsule = true,
  pripraveny = true,
  pociatocnaKategoria,
  oblubenePredvolene = false,
  akciaRiadku,
}: MaterialSelectProps): JSX.Element {
  const [search, setSearch] = useState("");
  const [otvorene, setOtvorene] = useState(false);
  /**
   * Filtre okna — kategória a obľúbené. Idú do parametrov API
   * (`kategoria`, `je_oblubene`) a **neprežijú zavretie okna** — je to
   * výber položky, nie nastavenie.
   */
  const [kategoria, setKategoria] = useState<string>(pociatocnaKategoria ?? ANY);
  // Kategória od volajúceho môže prísť až po otvorení (detail starého
  // kovania). Nastaví sa **ešte v tom istom renderi**, nie efektom — efekt
  // by stihol poslať dopyt so starou kategóriou (celé kovanie navyše).
  const [predoslaPociatocna, setPredoslaPociatocna] = useState(pociatocnaKategoria);
  if (pociatocnaKategoria !== predoslaPociatocna) {
    setPredoslaPociatocna(pociatocnaKategoria);
    if (pociatocnaKategoria) setKategoria(pociatocnaKategoria);
  }
  const [kategorieOtvorene, setKategorieOtvorene] = useState(false);
  // Obľúbené sú len pri kovaní (user po ukážke KOLA 201: *„obľúbené má
  // byť len na kovaní, nie všade"*) — dosky a hrany ich nemajú
  const ukazOblubene = sekcia === "kovanie";
  /**
   * Obľúbené **pri písaní samo vypne** (user, KOLO 202): kto píše, hľadá
   * v celom kovaní — dovtedy hľadanie obľúbené aj kategóriu ignorovalo,
   * teraz kategória ostane a obľúbené sa vypnú. Hľadanie má preto vlastný
   * prepínač (začína vypnutý, ★ ho zapne — hľadaj len v obľúbených) a po
   * zmazaní textu sa vráti stav spred písania. Dva stavy namiesto efektu,
   * ktorý by obľúbené vypínal: ten by stihol poslať jeden dopyt navyše.
   */
  const [lenOblubene, setLenOblubene] = useState(ukazOblubene && oblubenePredvolene);
  const [oblubeneVHladani, setOblubeneVHladani] = useState(false);
  /**
   * „Aj iní dodávatelia" (KOLO 166). Výber ponúka výrobok značky od
   * preferovaného dodávateľa (Blum → TWD); prepínač vráti aj riadky
   * ostatných — keď TWD nemá farbu či balenie, ktoré treba. Kódy
   * výrobcu má len kovanie, inde by prepínač nič nemenil.
   */
  const [ajIni, setAjIni] = useState(false);
  const ukazAjIni = !vsetciDodavatelia && (sekcia === undefined || sekcia === "kovanie");
  const debounced = useDebounce(search, 300);
  const strukturaLabels = useStrukturaLabels();

  // Rozmerové filtre platia **aj počas hľadania** (KOLO 120): pri položke
  // „8681 [18 mm]" stačilo začať písať a naskočili dosky všetkých hrúbok.
  // Kto chce vidieť ostatné, vypne kapsulu — vedome, nie tým, že píše.
  const [vypnute, setVypnute] = useState<ReadonlySet<RozmerKluc>>(new Set());
  // Nový rozmer od volajúceho (iná hrúbka dosky) filter znova zapne
  useEffect(() => {
    setVypnute(new Set());
  }, [zMm, triedaHrubky, sirkaMm, sirkaPostacuje, hrubkaMm]);
  const hrubkaZap = !vypnute.has("hrubka");
  const sirkaZap = !vypnute.has("sirka");
  const paskaZap = !vypnute.has("paska");
  const rozmery: { kluc: RozmerKluc; text: string; title: string }[] = [];
  if (rozmerKapsule) {
    if (zMm || triedaHrubky != null) {
      const h = zMm ? cisloMm(zMm) : String(triedaHrubky);
      rozmery.push({
        kluc: "hrubka",
        text: `hrúbka ${h} mm`,
        title: zMm
          ? `Len dosky hrúbky ${h} mm`
          : `Dosky hrúbky ${h} mm vrátane desatinných (${h},7 sa lepí ako ${h})`,
      });
    }
    if (sirkaPostacuje) {
      rozmery.push({
        kluc: "sirka",
        text: `šírka ${cisloMm(sirkaPostacuje)} mm a širšie`,
        title: "Hrana podľa hrúbky dielca — širšia dosku olepí tiež",
      });
    } else if (sirkaMm) {
      rozmery.push({
        kluc: "sirka",
        text: sirkaPasmo ? `šírka ${cisloMm(sirkaMm)} mm a blízke` : `šírka ${cisloMm(sirkaMm)} mm`,
        title: "Šírka hrany podľa hrúbky dielca",
      });
    }
    if (hrubkaMm) {
      rozmery.push({
        kluc: "paska",
        text: `hrúbka pásky ${cisloMm(hrubkaMm)} mm`,
        title: "Hrúbka ABS pásky",
      });
    }
  }
  const rozmerZapnuty = rozmery.some((r) => !vypnute.has(r.kluc));

  const searching = debounced.trim() !== "";
  const oblubeneZap = ukazOblubene && (searching ? oblubeneVHladani : lenOblubene);
  const spolocneFiltre = {
    search: debounced || undefined,
    druh,
    sekcia,
    bez_hran: bezHran || undefined,
    // Rozsah pickera (KOLO 166): čo sa smie ponúknuť, rozhoduje server
    // (`ponukatelne`) — výpredaj či nepreferovaný dodávateľ tak odídu
    // zo všetkých pickerov naraz, bez úpravy tu
    vyber: true,
    z_mm: (hrubkaZap && zMm) || undefined,
    trieda_hrubky: hrubkaZap ? triedaHrubky : undefined,
    sirka_mm: (sirkaZap && sirkaMm) || undefined,
    sirka_pasmo: sirkaZap && sirkaMm && sirkaPasmo ? true : undefined,
    sirka_postacuje: (sirkaZap && sirkaPostacuje) || undefined,
    sirka_trieda: sirkaTrieda,
    hrubka_mm: (paskaZap && hrubkaMm) || undefined,
    kategoria_include_csv: kategoriaIncludeCsv,
    // Kategória a obľúbené sú vôľa usera, rovnako ako rozmery
    kategoria: kategoria !== ANY ? kategoria : undefined,
    je_oblubene: oblubeneZap || undefined,
    // Platí aj pre návrhy (`zhody`) — tie server už presmeroval na
    // preferovaného, takže sa nestratia ani bez prepínača
    aj_ini_dodavatelia: vsetciDodavatelia || ajIni || undefined,
  };

  // Celé riadky k návrhom. Ide to cez **ten istý endpoint a tie isté
  // filtre**, takže nesprávnu hrúbku odfiltruje SQL a tento komponent
  // sa o ňu nestará. `enabled` je nutné: bez neho by položka bez
  // návrhov poslala `per_page=0`, čo endpoint odmietne (422) — a stav
  // „appka nič nenavrhla" je bežný (kovania `F001249`, nové dekory).
  const maZhody = Boolean(zhody && zhody.length > 0);
  const zhodyQuery = useCennikMaterialy(
    {
      ...spolocneFiltre,
      ids_csv: zhody?.map((z) => z.material_id).join(","),
      per_page: Math.min(Math.max(zhody?.length ?? 1, 1), 100),
    },
    { enabled: maZhody && pripraveny },
  );

  const query = useCennikMaterialyStrankovane(
    {
      ...spolocneFiltre,
      per_page: searching ? 30 : 20,
    },
    { enabled: pripraveny },
  );

  // Kategórie s počtami — tie isté fasety ako Cenník, s filtrami tohto
  // výberu (každá faseta bez svojho filtra). Pýtajú sa až pri prvom
  // otvorení rozbaľovačky: väčšina výberov ju nepotrebuje.
  const facetyQuery = useCennikFacety(spolocneFiltre, { enabled: kategorieOtvorene });
  const blokyKategorii = facetyQuery.data?.data
    ? kategorieMoznosti(facetyQuery.data.data, sekcia === "kovanie")
    : undefined;

  // Vybraný materiál sa doťahuje **detailom podľa id**, nie fulltextom
  // `search=<UUID>` — UUID v `search_text` nie je, takže ten dopyt
  // nevrátil nikdy nič a v pruhu svietilo skrátené UUID. Detail navyše
  // nemá filtre, takže nájde aj položku, ktorú zoznam skrýva.
  const selectedMaterialQuery = useMaterialDetail(vOkne ? undefined : (value ?? undefined));

  const vynechane = useMemo(() => new Set(vynechat ?? []), [vynechat]);

  // Zhody zoradené podľa skóre. Poradie z API sa nedá použiť —
  // `ids_csv` je `IN (…)`, ktoré poradie nezaručuje.
  const skoreMapa = useMemo(
    () => new Map((zhody ?? []).map((z) => [z.material_id, z])),
    [zhody],
  );
  const zhodneItems = useMemo(() => {
    if (!maZhody) return [] as Material[];
    return [...(zhodyQuery.data?.data ?? [])]
      .filter((m) => !vynechane.has(m.id))
      .sort((a, b) => (skoreMapa.get(b.id)?.skore ?? 0) - (skoreMapa.get(a.id)?.skore ?? 0));
  }, [maZhody, zhodyQuery.data, skoreMapa, vynechane]);

  // Skupiny nad cenníkom — hľadanie tým istým pravidlom ako server,
  // kategória a obľúbené presne; jedna položka len v prvej skupine
  const viditelneSkupiny = useMemo(() => {
    const uz = new Set<string>(vynechane);
    return (skupiny ?? []).map((s) => ({
      ...s,
      polozky: s.polozky.filter(({ material: m }) => {
        if (uz.has(m.id)) return false;
        if (
          !sediaSlova(debounced, [
            materialPrimaryId(m),
            m.nazov,
            m.alias,
            m.dekor_kod,
            m.struktura_povrchu,
            m.vyrobca,
            m.kategoria ? formatKategoria(m.kategoria) : null,
          ])
        ) {
          return false;
        }
        if (kategoria !== ANY && m.kategoria !== kategoria) return false;
        if (oblubeneZap && !m.je_oblubene) return false;
        uz.add(m.id);
        return true;
      }),
    }));
  }, [skupiny, vynechane, debounced, kategoria, oblubeneZap]);

  // Dedup ide proti tomu, čo sa **naozaj zobrazilo** hore, nie proti
  // `zhody`. Návrh, ktorý filter zahodil (iná hrúbka, iná kategória),
  // tak nezmizne z oboch polovíc naraz.
  const items = useMemo(() => {
    const zobrazene = new Set<string>([
      ...vynechane,
      ...zhodneItems.map((m) => m.id),
      ...viditelneSkupiny.flatMap((s) => s.polozky.map((p) => p.material.id)),
    ]);
    const vsetky = (query.data?.pages ?? []).flatMap((p) => p.data);
    return vsetky.filter((m) => !zobrazene.has(m.id));
  }, [query.data, zhodneItems, viditelneSkupiny, vynechane]);

  const celkom = query.data?.pages[0]?.pagination.total ?? 0;

  // Všetky viditeľné položky v poradí riadkov — pre šípky a výber
  const poradie: PolozkaCennika[] = [
    ...viditelneSkupiny.flatMap((s) => s.polozky.map((p) => p.material)),
    ...zhodneItems,
    ...items,
  ];
  const podlaId = new Map(poradie.map((m) => [m.id, m]));

  const detail = selectedMaterialQuery.data?.data ?? null;
  const selected: PolozkaCennika | null = value
    ? (podlaId.get(value) ?? (detail?.id === value ? detail : null))
    : null;

  // Kód pred názvom — bez kódu ostane názov; pomenované kovanie (alias)
  // ukáže alias, nie pravý názov (KOLO 176)
  const selectedKod = selected ? materialPrimaryId(selected) : null;
  const selectedNazov = selected ? menoKovania(selected).hlavne : "";
  const selectedName = value
    ? selected
      ? selectedKod
        ? `${selectedKod} — ${selectedNazov}`
        : selectedNazov
      : "Načítava sa…"
    : null;
  const selectedEshop = selected?.eshop_odkaz ?? null;

  // Zbalený picker s vybraným materiálom ukáže len ten jeden riadok —
  // hľadanie sa vráti až po „Zmeniť"
  const zbaleneSVybranym = kompaktny && !otvorene && Boolean(value);
  const zobrazZoznam = !kompaktny || otvorene;
  const otvor = (): void => {
    if (kompaktny) setOtvorene(true);
  };

  /** Plný materiál zo zoznamu cenníka; riadok skupiny je len súhrn. */
  function plny(m: PolozkaCennika): Material | undefined {
    return viditelneSkupiny.some((s) => s.polozky.some((p) => p.material === m))
      ? undefined
      : (m as Material);
  }

  function oznac(kluc: string, ako: AkoOznacene): void {
    const m = podlaId.get(kluc);
    if (!m) return;
    onChange(m.id, plny(m));
    // Klik vo formulári je výber — zbalený sa zbalí na jeden riadok
    if (kompaktny && !onPotvrd && ako === "klik") {
      setOtvorene(false);
      setSearch("");
    }
  }

  function potvrd(kluc: string): void {
    const m = podlaId.get(kluc);
    if (!m || !onPotvrd) return;
    onChange(m.id, plny(m));
    onPotvrd(m);
  }

  const najlepsieZhody = new Map(zhodneItems.map((m) => [m.id, skoreMapa.get(m.id)]));
  const tabulkaSkupiny: SkupinaVyberu[] = [
    ...viditelneSkupiny.map((s) => ({
      kluc: s.kluc,
      nadpis: s.nadpis,
      riadky: s.polozky.map((p) =>
        riadokPolozky(p.material, strukturaLabels, {
          stitky: p.stitky,
          info: p.info,
          akcia: akciaRiadku?.(p.material),
        }),
      ),
    })),
    // Návrhy len v importe — bez nich je cenník jediná skupina bez nadpisu
    ...(maZhody
      ? [
          {
            kluc: "navrhy",
            nadpis: "Návrhy",
            riadky: zhodneItems.map((m) =>
              riadokPolozky(m, strukturaLabels, {
                zhoda: najlepsieZhody.get(m.id),
                akcia: akciaRiadku?.(m),
              }),
            ),
          },
        ]
      : []),
    {
      kluc: "cennik",
      nadpis: "Ostatné z cenníka",
      riadky: items.map((m) => riadokPolozky(m, strukturaLabels, { akcia: akciaRiadku?.(m) })),
    },
  ];
  const kluce = poradie.map((m) => m.id);

  const nacitava =
    !pripraveny || query.isLoading || (maZhody && zhodyQuery.isLoading)
      ? viditelneSkupiny.every((s) => s.polozky.length === 0)
      : false;
  const prazdne = !nacitava && poradie.length === 0;
  const vKategorii = kategoria !== ANY ? " v tejto kategórii" : "";
  const stav = nacitava ? (
    "Načítavam…"
  ) : prazdne && oblubeneZap ? (
    // Náhrada starého prepínača „všetky v kategórii" — cesta k celému
    // kovaniu musí byť vidieť práve tam, kde obľúbené nič nemajú
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>
        {debounced ? "Medzi obľúbenými nič." : `Žiadne obľúbené${vKategorii}.`} Obľúbené sa
        pridávajú na stránke Kovania.
      </span>
      <button
        type="button"
        className="font-medium text-foreground underline underline-offset-2"
        onClick={() => (searching ? setOblubeneVHladani(false) : setLenOblubene(false))}
      >
        {kategoria !== ANY ? "Zobraziť všetky v kategórii" : "Zobraziť všetky kovania"}
      </button>
    </span>
  ) : prazdne ? (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>
        {debounced
          ? "Žiadne výsledky."
          : zMm || triedaHrubky != null
            ? `Žiadne dosky hrúbky ${zMm ?? triedaHrubky} mm.`
            : sirkaPostacuje
              ? `Žiadne hrany šírky ${sirkaPostacuje} mm a širšie.`
              : sirkaMm
                ? `Žiadne hrany šírky ${sirkaMm} mm.`
              : "Začni písať pre vyhľadanie."}
      </span>
      {/* Filter môže zoznam vyprázdniť — cesta späť musí byť vidieť */}
      {rozmerZapnuty ? (
        <button
          type="button"
          className="font-medium text-foreground underline underline-offset-2"
          onClick={() => setVypnute(new Set(rozmery.map((r) => r.kluc)))}
        >
          Zobraziť aj iné rozmery
        </button>
      ) : null}
    </span>
  ) : null;

  return (
    <div className={vOkne ? "flex min-h-0 flex-1 flex-col gap-2" : "space-y-2"}>
      {vOkne ? null : value && selectedName ? (
        <div className="flex items-center justify-between gap-2 rounded-md border bg-tandem-red-light px-3 py-2 text-sm">
          <span className="truncate">
            <span className="font-medium">Vybrané:</span> {selectedName}
          </span>
          {/* Kam si to pozrieť u dodávateľa. Odkaz skladá server
              (`odkaz_na_material`) — uložený priamy odkaz, inak
              vyhľadanie podľa šablóny; `KOD_PRE_ESHOP` sa do TS
              neprepisuje (lekcia z KOLA 77). */}
          {selectedEshop ? (
            <a
              href={selectedEshop}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 whitespace-nowrap text-xs underline underline-offset-2"
              title="Otvoriť položku v eshope dodávateľa"
            >
              Eshop ↗
            </a>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onChange(null);
              if (kompaktny) setOtvorene(true);
            }}
            type="button"
          >
            Zmeniť
          </Button>
        </div>
      ) : null}

      {zbaleneSVybranym ? null : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            aria-label={placeholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              // Nové hľadanie začína v celom kovaní
              if (!e.target.value.trim()) setOblubeneVHladani(false);
              otvor();
            }}
            onFocus={kompaktny ? otvor : undefined}
            onKeyDown={(e) => {
              if (kompaktny && e.key === "Escape") {
                e.stopPropagation();
                setOtvorene(false);
                return;
              }
              // Šípky chodia po riadkoch, Enter potvrdí — kurzor ostane
              // v poli, dá sa písať ďalej
              klavesaVyberu(e, {
                kluce,
                vybrany: value,
                onOznac: oznac,
                onPotvrd: onPotvrd ? potvrd : undefined,
              });
            }}
            className="h-10 pl-9"
            autoFocus={autoFocus}
          />
        </div>
      )}

      {/* Filtre v jednom riadku, vždy v tom istom poradí: Kategória ·
          rozmery · ďalšie · Obľúbené · Aj iní dodávatelia */}
      {zbaleneSVybranym ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <KategoriaVyberu
            bloky={blokyKategorii}
            hodnota={kategoria}
            ariaLabel="Kategória"
            onOtvorenie={() => setKategorieOtvorene(true)}
            zrusitelna
            onVyber={(h) => {
              setKategoria(h);
              otvor();
            }}
          />
          {rozmery.map((r) => {
            const zap = !vypnute.has(r.kluc);
            return (
              <button
                key={r.kluc}
                type="button"
                aria-pressed={zap}
                title={zap ? `${r.title} — klik filter vypne` : `${r.title} — klik filter zapne`}
                onClick={() => {
                  setVypnute((v) => {
                    const n = new Set(v);
                    if (zap) n.add(r.kluc);
                    else n.delete(r.kluc);
                    return n;
                  });
                  otvor();
                }}
                className={cn(
                  "inline-flex items-center rounded-full border border-input bg-background",
                  FILTER_TLACIDLO,
                  zap && FILTER_ZAPNUTY,
                )}
              >
                <span>{r.text}</span>
                {zap ? <X className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
              </button>
            );
          })}
          {filtreNavyse}
          {ukazOblubene ? (
            <button
              type="button"
              aria-pressed={oblubeneZap}
              title={
                searching
                  ? oblubeneZap
                    ? "Hľadá sa len v obľúbených — klik hľadá v celom kovaní"
                    : "Pri písaní sa hľadá v celom kovaní — klik hľadá len v obľúbených"
                  : "Ukáž len obľúbené položky"
              }
              onClick={() => {
                if (searching) setOblubeneVHladani((v) => !v);
                else setLenOblubene((v) => !v);
                otvor();
              }}
              className={cn(
                "inline-flex items-center rounded-full border border-input bg-background",
                FILTER_TLACIDLO,
                oblubeneZap && FILTER_ZAPNUTY,
              )}
            >
              <Star className={cn("h-3.5 w-3.5 shrink-0", oblubeneZap && "fill-current")} aria-hidden />
              <span>Obľúbené</span>
            </button>
          ) : null}
          {ukazAjIni ? (
            <button
              type="button"
              aria-pressed={ajIni}
              title="Ukáž aj ten istý výrobok od iných dodávateľov — inak sa ponúka len preferovaný (Nastavenia → Ceny a cenníky)"
              onClick={() => {
                setAjIni((v) => !v);
                otvor();
              }}
              className={cn(
                "inline-flex items-center rounded-full border border-input bg-background",
                FILTER_TLACIDLO,
                ajIni && FILTER_ZAPNUTY,
              )}
            >
              <span>Aj iní dodávatelia</span>
            </button>
          ) : null}
        </div>
      )}

      {!zobrazZoznam ? null : (
        <TabulkaVyberu
          skupiny={tabulkaSkupiny}
          vybrany={value}
          onOznac={oznac}
          onPotvrd={onPotvrd ? potvrd : undefined}
          soZhodou={maZhody}
          infoNadpis={infoNadpis}
          stav={stav}
          // V okne zoznam vyplní výšku okna; vo formulári je nižší,
          // s návrhmi (import) vyšší — je to hlavný prvok okna
          className={cn(
            vOkne
              ? "min-h-[10rem] flex-1"
              : maZhody || vysokyZoznam
                ? "max-h-[28rem]"
                : "max-h-64",
          )}
          pata={
            // Bez tohto zoznam končil na 20 (resp. 30) riadkoch a ďalej
            // sa nedalo dostať — `page` sa neposielal vôbec
            query.hasNextPage && !stav ? (
              <div className="border-t p-2 text-center">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-full text-xs"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {query.isFetchingNextPage
                    ? "Načítavam…"
                    : `Načítať ďalšie (zobrazených ${items.length + zhodneItems.length} z ${celkom})`}
                </Button>
              </div>
            ) : null
          }
        />
      )}
    </div>
  );
}
